// The bundled scores are catalogue documents like any other: same envelope,
// same directory, same validator. They used to carry a private `vlc-score`
// file format AND a hand-written TS table declaring their controller,
// provenance and rights — one document, two places to state what it is.
//
// Now the document is the source. This module is the PUBLICATION layer over
// it: revision, listing and summary, which are catalogue facts rather than
// file-format facts (see the ADR note in scoreCatalog.ts).
import { bundledDocuments } from '@/storage/bundledDocuments';
import { normalizeScore } from '@/music/score/normalize';
import type { Score } from '@/music/score/types';
import type { ScoreDoc } from '@/storage/documents';
import type { ScoreCatalogEntry, ScoreRecord } from './scoreCatalog';

export const ABLUTION_SCORE_ID = 'db766e15-cc12-47e8-bbb6-c4055fed752a';
export const ABLUTION_REVISION_ID = 'a8ad07a7-0a53-44ac-bb4a-2f126891ea97';
export const SHAM_TIME_SCORE_ID = 'chart-82cef88c-9541-4499-8c75-b2f1243af830';
export const SHAM_TIME_REVISION_ID = 'fdf3d18d-a1c0-490f-bb73-5e7224e4f1d0';
export const NIGHT_IN_TUNISIA_SCORE_ID = '88996a5e-ee43-4e65-bff3-466720816f5d';
export const NIGHT_IN_TUNISIA_REVISION_ID = '311bdf74-4b52-424f-b20f-da49abed6b8f';
export const CARAVAN_SCORE_ID = 'caravan-1h43wl2';
export const NICAS_DREAM_SCORE_ID = 'nica-s-dream-0vxg40q';
export const NICAS_DREAM_REVISION_ID = '5e2d8c14-9f70-42ab-b3c6-8a1704e5d9b8';
export const CARAVAN_REVISION_ID = 'c7b3e918-2a46-4d05-8f71-6e9c4b1a0d52';
export const GREEN_DOLPHIN_SCORE_ID = 'on-green-dolphin-street-0mqviwf';
export const GREEN_DOLPHIN_REVISION_ID = 'a4f1c0d2-6b57-4f3e-9c81-2d5e7a0b9f34';
export const LORO_SCORE_ID = 'loro-0tehg76';
export const LORO_REVISION_ID = 'bf0a29b9-d077-4733-983d-fb868499ab65';
export const SERENADE_SCORE_ID = 'serenade-to-a-cuckoo-0ei31m3';
export const SERENADE_REVISION_ID = '20d32761-9d6f-427d-a01a-4add5961281d';
export const GEORGES_DILEMMA_SCORE_ID = 'george-s-dilemma-0002th2';
export const GEORGES_DILEMMA_REVISION_ID = '12e863e4-4018-4bf5-9db2-00dbce6a4329';
export const ALONG_CAME_BETTY_SCORE_ID = 'along-came-betty-studio';
export const ALONG_CAME_BETTY_REVISION_ID = 'f7c3a9d0-5d62-4e81-a61e-8b34cbe7e194';
export const MOMENTS_NOTICE_SCORE_ID = 'moments-notice-studio';
export const MOMENTS_NOTICE_REVISION_ID = 'fe12cd8c-5a11-467d-9fc3-9613730b4c89';
export const STRASBOURG_ST_DENIS_SCORE_ID = 'strasbourg-st-denis-studio';
export const STRASBOURG_ST_DENIS_REVISION_ID = '4f2bcc73-390e-46f4-bfbf-926fd71a56a4';
export const ARMANDOS_RUMBA_SCORE_ID = 'armandos-rumba-studio';
export const ARMANDOS_RUMBA_REVISION_ID = '159a63bd-8b05-45f6-b705-fac62de93ac3';
export const STRANGE_MEETING_SCORE_ID = 'strange-meeting-studio';
export const STRANGE_MEETING_REVISION_ID = 'fd83a01b-f97d-4d15-ad95-f2b994550161';
export const TRICOTISM_SCORE_ID = 'tricotism-studio';
export const TRICOTISM_REVISION_ID = '8a4eb1f9-0d96-4699-a5ec-78591630e25a';
export const LADIES_IN_MERCEDES_SCORE_ID = 'ladies-in-mercedes-studio';
export const LADIES_IN_MERCEDES_REVISION_ID = '02514e2f-9f8a-4a32-a938-233f1cd1191d';
export const LADY_BIRD_SCORE_ID = 'lady-bird-studio';
export const LADY_BIRD_REVISION_ID = 'a2967b19-058e-4dd2-9bcc-fb948ee8d9a1';
export const LADY_BIRD_LEAD_SHEET_SCORE_ID = 'lady-bird-lead-sheet-studio';
export const LADY_BIRD_LEAD_SHEET_REVISION_ID = 'd3471f46-2729-4072-aa6c-88f7b08eac40';
export const NO_ROOM_FOR_SQUARES_SCORE_ID = 'no-room-for-squares-studio';
export const NO_ROOM_FOR_SQUARES_REVISION_ID = '3f6a1c2e-9b7d-4e58-a1c4-7d2e8b5f0a91';
export const NO_ROOM_FOR_SQUARES_TAKE_SCORE_ID = 'no-room-for-squares-1963-take-studio';
export const NO_ROOM_FOR_SQUARES_TAKE_REVISION_ID = '8c1d4f72-2e6a-4b0f-9d35-6a7e1c3b9f04';
export const JAMES_SCORE_ID = 'james-studio';
export const JAMES_REVISION_ID = '693bb1e4-c14e-4c67-9437-2a0147485c43';
export const JAMES_TAKE_SCORE_ID = 'james-offramp-take-studio';
export const JAMES_TAKE_REVISION_ID = 'e056d56f-d70f-43d6-8eae-8ea44925fa1a';
export const HOLLAND_THWAITES_BLUES_SCORE_ID = 'dave-holland-thwaites-blues-studio';
export const HOLLAND_THWAITES_BLUES_REVISION_ID = 'c1b45d50-3e7f-4ed8-bb30-11100bd13516';
export const GOD_ONLY_KNOWS_SCORE_ID = 'god-only-knows-studio';
export const GOD_ONLY_KNOWS_REVISION_ID = '13157a61-5518-4c54-913b-5080a2070da4';
export const GOD_ONLY_KNOWS_TAKE_SCORE_ID = 'god-only-knows-pet-sounds-take-studio';
export const GOD_ONLY_KNOWS_TAKE_REVISION_ID = 'edc3fa6a-ce1c-4b5f-a194-ef7a7053542c';

/** Every bundled score document, by id. */
const DOCUMENTS = new Map<string, ScoreDoc>(
  bundledDocuments()
    .filter((doc): doc is ScoreDoc => doc.kind === 'score')
    .map((doc) => [doc.id, doc]),
);

const parsed = new Map<string, Score>();

/**
 * The catalogue record for a bundled score, read from its document.
 *
 * Controller, provenance and rights are stated ONCE — in the file, beside the
 * music they describe. A missing document is a build error, not a silent
 * entry with no rights: an unassessed score must never look cleared.
 */
function recordOf(scoreId: string): ScoreRecord {
  const doc = DOCUMENTS.get(scoreId);
  if (!doc) throw new Error(`Bundled score document is missing: ${scoreId}`);
  return {
    id: doc.id,
    controller: doc.controller ?? { kind: 'system', id: 'voice-leading-calculator' },
    provenance: doc.provenance ?? { kind: 'original' },
    rights: doc.rights ?? { status: 'review-required' },
  };
}

/** Native scores curated in Score Studio. When publishing a new revision,
 * append the outgoing ID to previousRevisionIds and keep all earlier IDs.
 * These IDs offer explicit playlist upgrades; they do not alias old payloads.
 * Unknown IDs may belong to newer builds, so must not be overwritten. */
export const BUNDLED_SCORES: ScoreCatalogEntry[] = [
  {
    record: recordOf(GOD_ONLY_KNOWS_SCORE_ID),
    revision: {
      id: GOD_ONLY_KNOWS_REVISION_ID,
      scoreId: GOD_ONLY_KNOWS_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: GOD_ONLY_KNOWS_SCORE_ID,
      revisionId: GOD_ONLY_KNOWS_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'God Only Knows',
      composer: 'Brian Wilson / Tony Asher',
      style: 'Medium Swing',
      musicKey: 'E major',
      measures: 19,
      hasMelody: true,
      hasLyrics: true,
    },
    addedAt: '2026-10-26',
    asset: { kind: 'bundled', file: `${GOD_ONLY_KNOWS_SCORE_ID}.json` },
  },
  {
    record: recordOf(GOD_ONLY_KNOWS_TAKE_SCORE_ID),
    revision: {
      id: GOD_ONLY_KNOWS_TAKE_REVISION_ID,
      scoreId: GOD_ONLY_KNOWS_TAKE_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: GOD_ONLY_KNOWS_TAKE_SCORE_ID,
      revisionId: GOD_ONLY_KNOWS_TAKE_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'God Only Knows (Pet Sounds take)',
      composer: 'Brian Wilson / Tony Asher',
      style: 'Even 8ths',
      musicKey: 'E major',
      measures: 84,
      hasMelody: true,
      hasLyrics: true,
    },
    addedAt: '2026-10-26',
    asset: { kind: 'bundled', file: `${GOD_ONLY_KNOWS_TAKE_SCORE_ID}.json` },
  },
  {
    record: recordOf(HOLLAND_THWAITES_BLUES_SCORE_ID),
    revision: {
      id: HOLLAND_THWAITES_BLUES_REVISION_ID,
      scoreId: HOLLAND_THWAITES_BLUES_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: HOLLAND_THWAITES_BLUES_SCORE_ID,
      revisionId: HOLLAND_THWAITES_BLUES_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Blues in G (Dave Holland at Thwaites)',
      composer: 'Dave Holland',
      style: 'Medium Swing',
      musicKey: 'G major',
      measures: 14,
      hasMelody: true,
    },
    addedAt: '2026-10-25',
    asset: { kind: 'bundled', file: `${HOLLAND_THWAITES_BLUES_SCORE_ID}.json` },
  },
  {
    record: recordOf(JAMES_SCORE_ID),
    previousRevisionIds: ['dc78a4ac-c02d-4801-9627-6fd17db13356'],
    revision: {
      id: JAMES_REVISION_ID,
      scoreId: JAMES_SCORE_ID,
      number: 2,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: JAMES_SCORE_ID,
      revisionId: JAMES_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'James',
      composer: 'Pat Metheny / Lyle Mays',
      style: 'Even 8ths',
      musicKey: 'D major',
      measures: 44,
      hasMelody: true,
    },
    addedAt: '2026-10-23',
    asset: { kind: 'bundled', file: `${JAMES_SCORE_ID}.json` },
  },
  {
    record: recordOf(JAMES_TAKE_SCORE_ID),
    revision: {
      id: JAMES_TAKE_REVISION_ID,
      scoreId: JAMES_TAKE_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: JAMES_TAKE_SCORE_ID,
      revisionId: JAMES_TAKE_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'James (Offramp take)',
      composer: 'Pat Metheny / Lyle Mays',
      style: 'Even 8ths',
      musicKey: 'D major',
      measures: 244,
      hasMelody: true,
    },
    addedAt: '2026-10-23',
    asset: { kind: 'bundled', file: `${JAMES_TAKE_SCORE_ID}.json` },
  },
  {
    record: recordOf(NO_ROOM_FOR_SQUARES_SCORE_ID),
    revision: {
      id: NO_ROOM_FOR_SQUARES_REVISION_ID,
      scoreId: NO_ROOM_FOR_SQUARES_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: NO_ROOM_FOR_SQUARES_SCORE_ID,
      revisionId: NO_ROOM_FOR_SQUARES_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'No Room For Squares',
      composer: 'Hank Mobley',
      style: 'Up Tempo Swing',
      musicKey: 'C minor',
      measures: 48,
      hasMelody: true,
    },
    addedAt: '2026-10-18',
    asset: { kind: 'bundled', file: `${NO_ROOM_FOR_SQUARES_SCORE_ID}.json` },
  },
  {
    record: recordOf(NO_ROOM_FOR_SQUARES_TAKE_SCORE_ID),
    revision: {
      id: NO_ROOM_FOR_SQUARES_TAKE_REVISION_ID,
      scoreId: NO_ROOM_FOR_SQUARES_TAKE_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: NO_ROOM_FOR_SQUARES_TAKE_SCORE_ID,
      revisionId: NO_ROOM_FOR_SQUARES_TAKE_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'No Room For Squares (1963 take)',
      composer: 'Hank Mobley',
      style: 'Up Tempo Swing',
      musicKey: 'C minor',
      measures: 419,
      hasMelody: true,
    },
    addedAt: '2026-10-18',
    asset: { kind: 'bundled', file: `${NO_ROOM_FOR_SQUARES_TAKE_SCORE_ID}.json` },
  },
  {
    record: recordOf(LADY_BIRD_LEAD_SHEET_SCORE_ID),
    revision: {
      id: LADY_BIRD_LEAD_SHEET_REVISION_ID,
      scoreId: LADY_BIRD_LEAD_SHEET_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: LADY_BIRD_LEAD_SHEET_SCORE_ID,
      revisionId: LADY_BIRD_LEAD_SHEET_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Lady Bird',
      composer: 'Tadd Dameron',
      style: 'Medium Swing',
      musicKey: 'C major',
      measures: 16,
      hasMelody: true,
    },
    addedAt: '2026-10-18',
    asset: { kind: 'bundled', file: `${LADY_BIRD_LEAD_SHEET_SCORE_ID}.json` },
  },
  {
    record: recordOf(LADY_BIRD_SCORE_ID),
    previousRevisionIds: ['61e9dd1c-54c7-421d-9236-ec3105c87497'],
    revision: {
      id: LADY_BIRD_REVISION_ID,
      scoreId: LADY_BIRD_SCORE_ID,
      number: 2,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: LADY_BIRD_SCORE_ID,
      revisionId: LADY_BIRD_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Lady Bird (FeelBook take)',
      composer: 'Tadd Dameron',
      style: 'Medium Swing',
      musicKey: 'C major',
      measures: 225,
      hasMelody: true,
    },
    addedAt: '2026-10-18',
    asset: { kind: 'bundled', file: `${LADY_BIRD_SCORE_ID}.json` },
  },
  {
    record: recordOf(LADIES_IN_MERCEDES_SCORE_ID),
    revision: {
      id: LADIES_IN_MERCEDES_REVISION_ID,
      scoreId: LADIES_IN_MERCEDES_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: LADIES_IN_MERCEDES_SCORE_ID,
      revisionId: LADIES_IN_MERCEDES_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Ladies in Mercedes',
      composer: 'Steve Swallow',
      style: 'Medium Latin',
      musicKey: 'G major · D♭ major at bar 13',
      measures: 32,
      hasMelody: true,
    },
    addedAt: '2026-10-10',
    asset: { kind: 'bundled', file: `${LADIES_IN_MERCEDES_SCORE_ID}.json` },
  },
  {
    record: recordOf(TRICOTISM_SCORE_ID),
    previousRevisionIds: ['55f21776-0384-40c9-8cf1-459d08a2bf4c'],
    revision: {
      id: TRICOTISM_REVISION_ID,
      scoreId: TRICOTISM_SCORE_ID,
      number: 2,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: TRICOTISM_SCORE_ID,
      revisionId: TRICOTISM_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Tricotism',
      composer: 'Oscar Pettiford',
      style: 'Medium Swing',
      musicKey: 'D♭ major',
      measures: 33,
      hasMelody: true,
    },
    addedAt: '2026-10-03',
    asset: { kind: 'bundled', file: `${TRICOTISM_SCORE_ID}.json` },
  },
  {
    record: recordOf(STRANGE_MEETING_SCORE_ID),
    previousRevisionIds: ['bdff3443-b998-41cc-99af-ea440a3bbf32'],
    revision: {
      id: STRANGE_MEETING_REVISION_ID,
      scoreId: STRANGE_MEETING_SCORE_ID,
      number: 2,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: STRANGE_MEETING_SCORE_ID,
      revisionId: STRANGE_MEETING_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Strange Meeting (Version 1)',
      composer: 'Bill Frisell',
      style: 'Medium Tempo',
      musicKey: 'C minor',
      measures: 24,
      hasMelody: true,
    },
    addedAt: '2026-10-03',
    asset: { kind: 'bundled', file: `${STRANGE_MEETING_SCORE_ID}.json` },
  },
  {
    record: recordOf(ARMANDOS_RUMBA_SCORE_ID),
    previousRevisionIds: [
      'c58c1c36-5d14-4b73-b7bb-12ec3e7b1f83',
      '0af337dd-3dea-411b-be7e-aebf15833425',
      '2a7e3333-7b87-4761-baf6-9d9332245c41',
      '1689f489-28c2-4816-a442-71bdac90b569',
    ],
    revision: {
      id: ARMANDOS_RUMBA_REVISION_ID,
      scoreId: ARMANDOS_RUMBA_SCORE_ID,
      number: 5,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: ARMANDOS_RUMBA_SCORE_ID,
      revisionId: ARMANDOS_RUMBA_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: "Armando's Rumba",
      composer: 'Chick Corea',
      style: 'Flamenco style Rumba',
      musicKey: 'C minor',
      measures: 68,
      hasMelody: true,
    },
    addedAt: '2026-09-29',
    asset: { kind: 'bundled', file: `${ARMANDOS_RUMBA_SCORE_ID}.json` },
  },
  {
    record: recordOf(STRASBOURG_ST_DENIS_SCORE_ID),
    revision: {
      id: STRASBOURG_ST_DENIS_REVISION_ID,
      scoreId: STRASBOURG_ST_DENIS_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: STRASBOURG_ST_DENIS_SCORE_ID,
      revisionId: STRASBOURG_ST_DENIS_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Strasbourg-St. Denis',
      composer: 'Roy Hargrove',
      style: 'Funk',
      musicKey: 'A♭ major signature · B♭ minor centre',
      measures: 26,
      hasMelody: true,
    },
    addedAt: '2026-09-18',
    asset: { kind: 'bundled', file: `${STRASBOURG_ST_DENIS_SCORE_ID}.json` },
  },
  {
    record: recordOf(MOMENTS_NOTICE_SCORE_ID),
    revision: {
      id: MOMENTS_NOTICE_REVISION_ID,
      scoreId: MOMENTS_NOTICE_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: MOMENTS_NOTICE_SCORE_ID,
      revisionId: MOMENTS_NOTICE_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: "Moment's Notice",
      composer: 'John Coltrane',
      style: 'Bright Swing',
      musicKey: 'E♭ major',
      measures: 49,
      hasMelody: true,
    },
    addedAt: '2026-09-18',
    asset: { kind: 'bundled', file: `${MOMENTS_NOTICE_SCORE_ID}.json` },
  },
  {
    record: recordOf(ALONG_CAME_BETTY_SCORE_ID),
    revision: {
      id: ALONG_CAME_BETTY_REVISION_ID,
      scoreId: ALONG_CAME_BETTY_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: ALONG_CAME_BETTY_SCORE_ID,
      revisionId: ALONG_CAME_BETTY_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Along Came Betty',
      composer: 'Benny Golson',
      style: 'Medium Swing',
      musicKey: 'A♭ major · no printed key signature',
      measures: 34,
      hasMelody: true,
    },
    addedAt: '2026-09-18',
    asset: { kind: 'bundled', file: `${ALONG_CAME_BETTY_SCORE_ID}.json` },
  },
  {
    record: recordOf(ABLUTION_SCORE_ID),
    previousRevisionIds: ['49451004-cfbd-4006-b6ce-ce5bd9f5e5b9'],
    revision: {
      id: ABLUTION_REVISION_ID,
      scoreId: ABLUTION_SCORE_ID,
      number: 2,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: ABLUTION_SCORE_ID,
      revisionId: ABLUTION_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Ablution',
      composer: 'Lennie Tristano',
      style: 'Medium Swing',
      musicKey: 'A♭ major',
      measures: 36,
      hasMelody: true,
    },
    addedAt: '2026-07-23',
    asset: { kind: 'bundled', file: `${ABLUTION_SCORE_ID}.json` },
  },
  {
    record: recordOf(NIGHT_IN_TUNISIA_SCORE_ID),
    previousRevisionIds: ['45660ca8-e2b6-45c6-b660-d21c1075e5ca'],
    revision: {
      id: NIGHT_IN_TUNISIA_REVISION_ID,
      scoreId: NIGHT_IN_TUNISIA_SCORE_ID,
      number: 2,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: NIGHT_IN_TUNISIA_SCORE_ID,
      revisionId: NIGHT_IN_TUNISIA_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'A Night In Tunisia',
      composer: "John 'Dizzy' Gillespie, Frank Paparelli",
      style: 'Bright Swing',
      musicKey: 'F major / D minor',
      measures: 26,
      hasMelody: true,
    },
    addedAt: '2026-07-23',
    asset: { kind: 'bundled', file: `${NIGHT_IN_TUNISIA_SCORE_ID}.json` },
  },
  {
    record: recordOf(CARAVAN_SCORE_ID),
    revision: {
      id: CARAVAN_REVISION_ID,
      scoreId: CARAVAN_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: CARAVAN_SCORE_ID,
      revisionId: CARAVAN_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Caravan',
      composer: 'Duke Ellington, Juan Tizol',
      style: 'Latin/Swing',
      musicKey: 'F minor',
      measures: 48,
      hasMelody: true,
      hasLyrics: true,
    },
    addedAt: '2026-07-28',
    asset: { kind: 'bundled', file: `${CARAVAN_SCORE_ID}.json` },
  },
  {
    record: recordOf(NICAS_DREAM_SCORE_ID),
    revision: {
      id: NICAS_DREAM_REVISION_ID,
      scoreId: NICAS_DREAM_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: NICAS_DREAM_SCORE_ID,
      revisionId: NICAS_DREAM_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: "Nica's Dream",
      composer: 'Horace Silver',
      style: 'Latin/Swing',
      musicKey: 'D♭ major',
      measures: 52,
      hasMelody: true,
    },
    addedAt: '2026-07-29',
    asset: { kind: 'bundled', file: `${NICAS_DREAM_SCORE_ID}.json` },
  },
  {
    record: recordOf(GREEN_DOLPHIN_SCORE_ID),
    revision: {
      id: GREEN_DOLPHIN_REVISION_ID,
      scoreId: GREEN_DOLPHIN_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: GREEN_DOLPHIN_SCORE_ID,
      revisionId: GREEN_DOLPHIN_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'On Green Dolphin Street',
      composer: 'Bronislau Kaper',
      style: 'Latin/Swing',
      musicKey: 'C major',
      measures: 24,
      hasMelody: true,
      hasLyrics: true,
    },
    addedAt: '2026-07-28',
    asset: { kind: 'bundled', file: `${GREEN_DOLPHIN_SCORE_ID}.json` },
  },
  {
    record: recordOf(LORO_SCORE_ID),
    revision: {
      id: LORO_REVISION_ID,
      scoreId: LORO_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: LORO_SCORE_ID,
      revisionId: LORO_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Lôro',
      composer: 'Egberto Gismonti',
      style: 'Choro',
      musicKey: 'E♭ major',
      measures: 39,
      hasMelody: true,
    },
    addedAt: '2026-07-28',
    asset: { kind: 'bundled', file: `${LORO_SCORE_ID}.json` },
  },
  {
    record: recordOf(SERENADE_SCORE_ID),
    revision: {
      id: SERENADE_REVISION_ID,
      scoreId: SERENADE_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: SERENADE_SCORE_ID,
      revisionId: SERENADE_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Serenade to a Cuckoo',
      composer: 'Rahsaan Roland Kirk',
      style: 'Medium Swing',
      musicKey: 'F minor',
      measures: 18,
      hasMelody: true,
    },
    addedAt: '2026-07-28',
    asset: { kind: 'bundled', file: `${SERENADE_SCORE_ID}.json` },
  },
  {
    // The muscriptor path end to end (transcription slices 1-3): the Kyutai
    // model's MusicXML of the Study in Brown recording, re-barred from 3/4 to
    // 4/4, keyed C# minor, respelled, and its changes DETECTED from the piano
    // and bass content — the first tune whose chart nobody typed.
    record: recordOf(GEORGES_DILEMMA_SCORE_ID),
    previousRevisionIds: [
      '38854af2-9b8f-478a-b91c-563929a9f886',
      'ef001c89-f6cd-418d-92b2-355fc0b61c7f',
    ],
    revision: {
      id: GEORGES_DILEMMA_REVISION_ID,
      scoreId: GEORGES_DILEMMA_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: GEORGES_DILEMMA_SCORE_ID,
      revisionId: GEORGES_DILEMMA_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: "George's Dilemma",
      composer: 'Clifford Brown',
      style: 'Latin',
      musicKey: 'C♯ minor',
      measures: 40,
      hasMelody: true,
    },
    addedAt: '2026-08-28',
    asset: { kind: 'bundled', file: `${GEORGES_DILEMMA_SCORE_ID}.json` },
  },
  {
    record: recordOf(SHAM_TIME_SCORE_ID),
    revision: {
      id: SHAM_TIME_REVISION_ID,
      scoreId: SHAM_TIME_SCORE_ID,
      number: 1,
      payloadVersion: 1,
    },
    listing: {
      collection: 'bundled',
      scoreId: SHAM_TIME_SCORE_ID,
      revisionId: SHAM_TIME_REVISION_ID,
      status: 'active',
    },
    summary: {
      title: 'Sham Time',
      composer: 'Eddie Harris',
      musicKey: 'E♭ major',
      measures: 16,
      hasMelody: true,
    },
    addedAt: '2026-09-10',
    asset: { kind: 'bundled', file: `${SHAM_TIME_SCORE_ID}.json` },
  },
];

export function bundledScoreById(scoreId: string): ScoreCatalogEntry | undefined {
  return BUNDLED_SCORES.find((entry) => entry.record.id === scoreId);
}

export async function loadBundledScore(
  entryOrId: ScoreCatalogEntry | string,
  revisionId?: string,
): Promise<Score> {
  const entry = typeof entryOrId === 'string' ? bundledScoreById(entryOrId) : entryOrId;
  if (!entry || (revisionId && revisionId !== entry.revision.id)) {
    throw new Error('That bundled score revision is not available.');
  }
  let score = parsed.get(entry.record.id);
  if (!score) {
    const doc = DOCUMENTS.get(entry.record.id);
    if (!doc) throw new Error(`Bundled score asset is missing: ${entry.asset.file}`);
    score = normalizeScore(doc.score);
    parsed.set(entry.record.id, score);
  }
  return structuredClone(score);
}
